import { useState, useCallback, useEffect } from 'react';
import { Copy, RefreshCw } from 'lucide-react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Input';
import { Slider } from '@/components/ui/Slider';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { copyToClipboard } from '@/lib/utils';
import { toast } from 'sonner';

type Format = 'hex' | 'base64' | 'base64url' | 'base58';

const BASE58 = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';

function bytesToBase58(bytes: Uint8Array): string {
  let s = '';
  // Approximate; suitable for short tokens
  let value = 0n;
  for (const b of bytes) value = value * 256n + BigInt(b);
  while (value > 0n) {
    s = BASE58[Number(value % 58n)] + s;
    value = value / 58n;
  }
  return s || BASE58[0];
}

export function SecretGenerator() {
  const [bytes, setBytes] = useState(32);
  const [format, setFormat] = useState<Format>('hex');
  const [secret, setSecret] = useState('');

  const regen = useCallback(() => {
    const arr = crypto.getRandomValues(new Uint8Array(bytes));
    let s = '';
    if (format === 'hex') {
      s = Array.from(arr, (b) => b.toString(16).padStart(2, '0')).join('');
    } else if (format === 'base64') {
      let bin = '';
      for (let i = 0; i < arr.length; i++) bin += String.fromCharCode(arr[i]);
      s = btoa(bin);
    } else if (format === 'base64url') {
      let bin = '';
      for (let i = 0; i < arr.length; i++) bin += String.fromCharCode(arr[i]);
      s = btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    } else if (format === 'base58') {
      s = bytesToBase58(arr);
    }
    setSecret(s);
  }, [bytes, format]);

  useEffect(() => {
    regen();
  }, [regen]);

  return (
    <div>
      <ToolHeader
        toolId="secret-generator"
        title="Secret generator"
        description="Cryptographic secrets in hex, Base64, Base64URL, or Base58. Use for API keys, JWT secrets, session tokens."
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 !p-6">
          <div className="font-mono text-xl break-all min-h-[3rem]">{secret || '…'}</div>
          <div className="mt-4 flex items-center gap-2">
            <Button variant="primary" onClick={regen}>
              <RefreshCw size={12} />
              Regenerate
            </Button>
            <Button
              variant="secondary"
              onClick={async () => {
                const ok = await copyToClipboard(secret);
                if (ok) toast.success('Secret copied');
              }}
            >
              <Copy size={12} />
              Copy
            </Button>
            <span className="ml-auto text-2xs text-fg-subtle font-mono">
              {bytes * 8} bits · {secret.length} chars
            </span>
          </div>
        </Card>
        <Card className="space-y-3">
          <Field label="Bytes" hint={`${bytes}`}>
            <Slider value={bytes} onChange={setBytes} min={8} max={64} step={4} showValue={false} />
          </Field>
          <Tabs value={format} onValueChange={(v) => setFormat(v as Format)}>
            <TabsList className="w-full flex-wrap !h-auto">
              <TabsTrigger value="hex" className="flex-1">hex</TabsTrigger>
              <TabsTrigger value="base64" className="flex-1">base64</TabsTrigger>
              <TabsTrigger value="base64url" className="flex-1">b64url</TabsTrigger>
              <TabsTrigger value="base58" className="flex-1">base58</TabsTrigger>
            </TabsList>
          </Tabs>
        </Card>
      </div>
    </div>
  );
}

export default SecretGenerator;
